import React, { useState, useEffect } from "react";
import { Modal, Checkbox, Input, Space, Typography } from "antd";
import { FormattedMessage } from "react-intl";

const { Text } = Typography;
const { TextArea } = Input;

interface RTSCModalProps {
  open: boolean;
  orderId: number | null;
  isChargeable?: boolean;
  loading?: boolean;
  onConfirm: (params: { orderId: number; isExchange: boolean; returnReason: string }) => void;
  onCancel: () => void;
}

function RTSCModal({
  open,
  orderId,
  isChargeable = true,
  loading = false,
  onConfirm,
  onCancel,
}: RTSCModalProps) {
  const [isExchange, setIsExchange] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // ✅ NEW

  // ✅ Reset all state when modal opens
  useEffect(() => {
    if (open) {
      setIsExchange(false);
      setReturnReason("");
      setIsSubmitting(false);
    }
  }, [open, orderId]);

  const handleConfirm = () => {
    if (!orderId || isSubmitting) return;
    setIsSubmitting(true); // ✅ disable after first click
    onConfirm({ orderId, isExchange, returnReason });
  };

  const handleCancel = () => {
    setIsExchange(false);
    setReturnReason("");
    setIsSubmitting(false);
    onCancel();
  };

  return (
    <Modal
      title={
        isChargeable
          ? "Return to Shipper Chargeable (RTSC)"
          : "Return to Shipper (RTS)"
      }
      visible={open}
      onOk={handleConfirm}
      onCancel={handleCancel}
      okText={
        isChargeable
          ? <FormattedMessage id="button.confirmRTSC" defaultMessage="Confirm RTSC" />
          : <FormattedMessage id="button.confirmRTS" defaultMessage="Confirm RTS" />
      }
      cancelText={
        <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
      }
      confirmLoading={loading || isSubmitting} // ✅ spinner while submitting
      okButtonProps={{
        danger: true,
        disabled: isSubmitting, // ✅ disabled after first click
      }}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">

        <Text type="secondary">
          {isChargeable
            ? "The driver arrived at the customer location. The delivery charge will be applied to the retailer."
            : "The order will be returned to the shipper."
          }
        </Text>

        <Checkbox
          checked={isExchange}
          onChange={(e) => setIsExchange(e.target.checked)}
        >
          <FormattedMessage
            id="rtsc.modal.isExchange"
            defaultMessage="Exchange order (some items delivered, some returned)"
          />
        </Checkbox>

        <div>
          <Text strong style={{ display: "block", marginBottom: 6 }}>
            <FormattedMessage
              id="rtsc.modal.reason"
              defaultMessage="Return reason"
            />
            {" "}
            <Text type="secondary" style={{ fontWeight: 400 }}>
              <FormattedMessage id="optional" defaultMessage="(optional)" />
            </Text>
          </Text>
          <TextArea
            rows={3}
            placeholder="e.g. Customer refused delivery after driver arrived"
            value={returnReason}
            onChange={(e) => setReturnReason(e.target.value)}
            maxLength={200}
            showCount
          />
        </div>

      </Space>
    </Modal>
  );
}

export default RTSCModal;